import React, { useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight, MapPin, Compass, Mountain, Tent, Building2, Waves,
  Plane, Car, Loader2, Check, Calendar, Users, Sparkles, Heart,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFormatPrice } from "@/contexts/CurrencyContext";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";
import PageStickyNav from "@/components/PageStickyNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg";

const COPY = {
  hero_overline: { en: "Trips & Journeys", fr: "Voyages & Itinéraires", es: "Viajes e Itinerarios" },
  hero_title: { en: "Discover the soul", fr: "Découvrez l'âme", es: "Descubra el alma" },
  hero_accent: { en: "of Morocco.", fr: "du Maroc.", es: "de Marruecos." },
  hero_sub: {
    en: "From Saharan caravans to Atlantic surf villages, from imperial cities to Atlas summits — handcrafted journeys designed and operated by Grup Xaluca, with our own hotels, fleet and on-the-ground agencies in Erfoud, Merzouga, Ouarzazate and Boumalne Dades.",
    fr: "Des caravanes sahariennes aux villages de surf atlantiques, des villes impériales aux sommets de l'Atlas — des voyages cousus main, conçus et opérés par le Grup Xaluca, avec nos propres hôtels, notre flotte et nos agences sur place à Erfoud, Merzouga, Ouarzazate et Boumalne Dades.",
    es: "De las caravanas saharianas a las aldeas surferas del Atlántico, de las ciudades imperiales a las cumbres del Atlas — viajes hechos a mano, diseñados y operados por Grup Xaluca, con nuestros propios hoteles, flota y agencias locales en Erfoud, Merzouga, Ouarzazate y Boumalne Dades.",
  },
  hero_primary: { en: "Plan my trip", fr: "Planifier mon voyage", es: "Planificar mi viaje" },
  hero_secondary: { en: "Browse categories", fr: "Voir les catégories", es: "Ver categorías" },

  intro_overline: { en: "How we travel", fr: "Notre façon de voyager", es: "Nuestra forma de viajar" },
  intro_title: {
    en: "One operator, every corner of the south",
    fr: "Un seul opérateur, tous les recoins du sud",
    es: "Un solo operador, cada rincón del sur",
  },
  intro_text: {
    en: "Grup Xaluca is the only group in southern Morocco that owns the hotels, the bivouacs, the 4x4 fleet and the on-the-ground travel agencies. From the moment you land in Fez, Marrakech or Errachidia until the final wave goodbye at the airport, the same team carries the bags, drives the routes, books the spa slot and lights the campfire. Trips are sold as private journeys or scheduled group departures.",
    fr: "Le Grup Xaluca est le seul groupe du sud marocain à posséder à la fois les hôtels, les bivouacs, la flotte 4x4 et les agences de voyage locales. Dès votre arrivée à Fès, Marrakech ou Errachidia jusqu'au dernier signe à l'aéroport, la même équipe porte les bagages, conduit les routes, réserve le soin spa et allume le feu de camp. Les voyages se vendent en privatif ou en départs groupés programmés.",
    es: "Grup Xaluca es el único grupo del sur de Marruecos que posee a la vez los hoteles, los vivacs, la flota 4x4 y las agencias de viajes locales. Desde que aterriza en Fez, Marrakech o Errachidia hasta el último adiós en el aeropuerto, el mismo equipo carga las maletas, conduce las rutas, reserva el spa y enciende la hoguera. Los viajes se venden como privados o en salidas grupales programadas.",
  },
  brand_pillars: [
    {
      icon: Building2,
      title: { en: "Our hotels & bivouacs", fr: "Nos hôtels & bivouacs", es: "Nuestros hoteles y vivacs" },
      text: { en: "13 properties across the Sahara, the Atlas and the kasbah valleys — beds we own and rooms we know.", fr: "13 propriétés au Sahara, dans l'Atlas et les vallées des kasbahs — des lits qui nous appartiennent et des chambres que nous connaissons.", es: "13 propiedades en el Sáhara, el Atlas y los valles de las kasbahs — camas propias y habitaciones que conocemos." },
    },
    {
      icon: Car,
      title: { en: "Xaluca Transport", fr: "Xaluca Transport", es: "Xaluca Transport" },
      text: { en: "Our own fleet of 4x4 Toyotas, minivans and coaches with English-, French- and Spanish-speaking drivers.", fr: "Notre propre flotte de 4x4 Toyota, minivans et autocars avec chauffeurs anglophones, francophones et hispanophones.", es: "Nuestra propia flota de 4x4 Toyota, minivans y autocares con conductores que hablan inglés, francés y español." },
    },
    {
      icon: MapPin,
      title: { en: "Xaluca Travel Agencies", fr: "Agences Xaluca", es: "Agencias Xaluca" },
      text: { en: "Licensed agencies in Erfoud, Merzouga, Ouarzazate and Boumalne Dades — 24/7 local concierge during your trip.", fr: "Agences agréées à Erfoud, Merzouga, Ouarzazate et Boumalne Dades — conciergerie locale 24/7 pendant votre voyage.", es: "Agencias autorizadas en Erfoud, Merzouga, Ouarzazate y Boumalne Dades — conserjería local 24/7 durante su viaje." },
    },
  ],

  cats_overline: { en: "Trip categories", fr: "Catégories de voyages", es: "Categorías de viajes" },
  cats_title: { en: "Six ways to cross the south", fr: "Six manières de traverser le sud", es: "Seis maneras de cruzar el sur" },
  cats_sub: {
    en: "Pick a category — or mix and match. Every itinerary is private and adjusted to your pace, your team and the season.",
    fr: "Choisissez une catégorie — ou combinez-les. Chaque itinéraire est privatif et ajusté à votre rythme, votre équipe et la saison.",
    es: "Elija una categoría — o combínelas. Cada itinerario es privado y ajustado a su ritmo, su equipo y la temporada.",
  },
  categories: [
    {
      key: "desert",
      icon: Tent,
      title: { en: "Desert Safaris", fr: "Safaris du désert", es: "Safaris del desierto" },
      duration: { en: "3 to 7 days", fr: "3 à 7 jours", es: "3 a 7 días" },
      from: { en: "From €890 / guest", fr: "À partir de 890 € / pers.", es: "Desde 890 € / pers." },
      desc: {
        en: "Caravans across the Erg Chebbi dunes, nights under camel-hair haimas and white luxury suite tents, sunrises on the highest crests, Khamlia Gnawa music and meteor showers.",
        fr: "Caravanes à travers les dunes de l'Erg Chebbi, nuits sous haimas en poil de chameau et tentes-suites blanches, levers de soleil sur les plus hautes crêtes, musique gnaoua de Khamlia et pluies de météores.",
        es: "Caravanas por las dunas del Erg Chebbi, noches bajo haimas de pelo de camello y tiendas-suite blancas, amaneceres en las crestas más altas, música gnaoua de Khamlia y lluvias de meteoros.",
      },
      highlights: [
        { en: "Erg Chebbi camel caravan", fr: "Caravane Erg Chebbi", es: "Caravana Erg Chebbi" },
        { en: "Luxury bivouac nights", fr: "Nuits bivouac de luxe", es: "Noches en vivac de lujo" },
        { en: "Khamlia Gnawa evening", fr: "Soirée gnaoua à Khamlia", es: "Velada gnaoua en Khamlia" },
      ],
      image: "https://images.pexels.com/photos/30374217/pexels-photo-30374217.jpeg?auto=compress&cs=tinysrgb&w=1600",
    },
    {
      key: "atlas",
      icon: Mountain,
      title: { en: "Atlas Mountain Adventures", fr: "Aventures dans l'Atlas", es: "Aventuras en el Atlas" },
      duration: { en: "4 to 8 days", fr: "4 à 8 jours", es: "4 a 8 días" },
      from: { en: "From €1 100 / guest", fr: "À partir de 1 100 € / pers.", es: "Desde 1 100 € / pers." },
      desc: {
        en: "Trekking the M'Goun valley with a Berber guide and a mule, lake-side nights at 2 100 m in Sidi Ali, snowshoe routes in winter, climbing in the Todra Gorges, and tea at altitude.",
        fr: "Trek dans la vallée du M'Goun avec guide berbère et mule, nuits au bord du lac à 2 100 m à Sidi Ali, raquettes en hiver, escalade dans les Gorges du Todra et thé en altitude.",
        es: "Trekking por el valle del M'Goun con guía bereber y mula, noches junto al lago a 2 100 m en Sidi Ali, rutas con raquetas en invierno, escalada en las Gargantas del Todra y té en altura.",
      },
      highlights: [
        { en: "M'Goun trek with Berber guide", fr: "Trek M'Goun avec guide berbère", es: "Trek M'Goun con guía bereber" },
        { en: "Todra & Dades Gorges", fr: "Gorges du Todra & du Dades", es: "Gargantas del Todra y del Dades" },
        { en: "Cedar forest stargazing", fr: "Étoiles dans la cédraie", es: "Estrellas en el cedral" },
      ],
      image: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg",
    },
    {
      key: "cultural",
      icon: Compass,
      title: { en: "Cultural Journeys", fr: "Voyages culturels", es: "Viajes culturales" },
      duration: { en: "5 to 10 days", fr: "5 à 10 jours", es: "5 a 10 días" },
      from: { en: "From €1 250 / guest", fr: "À partir de 1 250 € / pers.", es: "Desde 1 250 € / pers." },
      desc: {
        en: "Aït Ben Haddou's UNESCO ksar, the Rissani souk, the artisans of Tinghir, weaving cooperatives, pottery in Tamegroute, and tea ceremonies in family kasbahs — with a heritage guide.",
        fr: "Le ksar UNESCO d'Aït Ben Haddou, le souk de Rissani, les artisans de Tinghir, coopératives de tissage, poterie à Tamegroute et cérémonies du thé chez l'habitant — avec un guide patrimonial.",
        es: "El ksar UNESCO de Aït Ben Haddou, el zoco de Rissani, los artesanos de Tinghir, cooperativas de tejido, cerámica en Tamegroute y ceremonias del té en kasbahs familiares — con un guía patrimonial.",
      },
      highlights: [
        { en: "Aït Ben Haddou UNESCO ksar", fr: "Ksar Aït Ben Haddou (UNESCO)", es: "Ksar Aït Ben Haddou (UNESCO)" },
        { en: "Berber weaving cooperative", fr: "Coopérative de tissage", es: "Cooperativa de tejido" },
        { en: "Tea with the families", fr: "Thé chez l'habitant", es: "Té con las familias" },
      ],
      image: "https://images.unsplash.com/photo-1539020140153-e479b8c22e70?auto=format&fit=crop&w=1600&q=80",
    },
    {
      key: "coastal",
      icon: Waves,
      title: { en: "Coastal Adventures", fr: "Escapades océanes", es: "Escapadas atlánticas" },
      duration: { en: "3 to 6 days", fr: "3 à 6 jours", es: "3 a 6 días" },
      from: { en: "From €780 / guest", fr: "À partir de 780 € / pers.", es: "Desde 780 € / pers." },
      desc: {
        en: "Essaouira's blue medina, oyster lunches in Oualidia, Taghazout surf and yoga, golden sunsets over the Atlantic — a soft counterpoint to the desert and mountain extremes.",
        fr: "La médina bleue d'Essaouira, déjeuner d'huîtres à Oualidia, surf et yoga à Taghazout, couchers de soleil dorés sur l'Atlantique — un contrepoint doux aux extrêmes du désert et de la montagne.",
        es: "La medina azul de Essaouira, almuerzo de ostras en Oualidia, surf y yoga en Taghazout, atardeceres dorados sobre el Atlántico — un contrapunto suave a los extremos del desierto y la montaña.",
      },
      highlights: [
        { en: "Essaouira blue medina", fr: "Médina bleue d'Essaouira", es: "Medina azul de Essaouira" },
        { en: "Taghazout surf & yoga", fr: "Surf & yoga à Taghazout", es: "Surf y yoga en Taghazout" },
        { en: "Atlantic sunset dinners", fr: "Dîners face à l'Atlantique", es: "Cenas frente al Atlántico" },
      ],
      image: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=80",
    },
    {
      key: "imperial",
      icon: Building2,
      title: { en: "Imperial Cities & Medinas", fr: "Cités impériales & médinas", es: "Ciudades imperiales y medinas" },
      duration: { en: "6 to 9 days", fr: "6 à 9 jours", es: "6 a 9 días" },
      from: { en: "From €1 480 / guest", fr: "À partir de 1 480 € / pers.", es: "Desde 1 480 € / pers." },
      desc: {
        en: "Fez, Meknès, Marrakech and the Volubilis Roman ruins — riad nights, palace dinners, private hammam rituals and a curated tour of the souks with a historian who has lived in the medina for thirty years.",
        fr: "Fès, Meknès, Marrakech et les ruines romaines de Volubilis — nuits en riad, dîners en palais, rituels privés au hammam et visite des souks avec un historien qui vit dans la médina depuis trente ans.",
        es: "Fez, Mequinez, Marrakech y las ruinas romanas de Volubilis — noches en riad, cenas de palacio, rituales privados de hammam y visita curada de los zocos con un historiador que vive en la medina desde hace treinta años.",
      },
      highlights: [
        { en: "Fez tannery & palace dinners", fr: "Tannerie de Fès & dîners en palais", es: "Curtidurías de Fez y cenas de palacio" },
        { en: "Riad nights in three cities", fr: "Nuits en riad dans trois villes", es: "Noches en riad en tres ciudades" },
        { en: "Volubilis Roman ruins", fr: "Ruines romaines de Volubilis", es: "Ruinas romanas de Volubilis" },
      ],
      image: "https://images.unsplash.com/photo-1597306599365-957bd6dc3ec5?auto=format&fit=crop&w=1600&q=80",
    },
    {
      key: "grand",
      icon: Sparkles,
      title: { en: "Grand Tours", fr: "Grands tours", es: "Grandes Tours" },
      duration: { en: "10 to 16 days", fr: "10 à 16 jours", es: "10 a 16 días" },
      from: { en: "From €2 950 / guest", fr: "À partir de 2 950 € / pers.", es: "Desde 2 950 € / pers." },
      desc: {
        en: "The full sweep: Imperial cities, High Atlas, kasbah valleys, Sahara dunes and Atlantic coast — one private journey, one driver-guide, all four of our agencies in your corner.",
        fr: "L'arc complet : cités impériales, Haut Atlas, vallées des kasbahs, dunes du Sahara et côte atlantique — un seul voyage privatif, un seul chauffeur-guide, nos quatre agences à vos côtés.",
        es: "El arco completo: ciudades imperiales, Alto Atlas, valles de las kasbahs, dunas del Sáhara y costa atlántica — un solo viaje privado, un solo chófer-guía, nuestras cuatro agencias a su lado.",
      },
      highlights: [
        { en: "All four regions", fr: "Les quatre régions", es: "Las cuatro regiones" },
        { en: "Same driver-guide throughout", fr: "Même chauffeur-guide tout du long", es: "Mismo chófer-guía en todo el viaje" },
        { en: "Personal trip designer", fr: "Concepteur de voyage personnel", es: "Diseñador de viaje personal" },
      ],
      image: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/6pvzp897_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-1.jpg",
    },
  ],

  process_overline: { en: "How a trip is built", fr: "Comment un voyage se construit", es: "Cómo se construye un viaje" },
  process_title: {
    en: "From wanderlust to wheels-up",
    fr: "De l'envie d'évasion au décollage",
    es: "Del deseo de viajar al despegue",
  },
  process_steps: [
    { num: "01", title: { en: "Free 30-min trip-design call", fr: "Appel design 30 min offert", es: "Llamada de diseño gratuita (30 min)" }, text: { en: "We listen to your dates, your group, your travel mood and your non-negotiables.", fr: "Nous écoutons vos dates, votre groupe, votre humeur de voyage et vos non-négociables.", es: "Escuchamos sus fechas, su grupo, su estado de ánimo de viaje y sus innegociables." } },
    { num: "02", title: { en: "Custom itinerary in 7 days", fr: "Itinéraire sur mesure sous 7 jours", es: "Itinerario a medida en 7 días" }, text: { en: "Map, day-by-day breakdown, hotels, vehicles, guides and a single all-inclusive quote.", fr: "Carte, déroulé jour par jour, hôtels, véhicules, guides et un devis unique tout inclus.", es: "Mapa, desglose día por día, hoteles, vehículos, guías y una única cotización todo incluido." } },
    { num: "03", title: { en: "On the road with us", fr: "Sur la route avec nous", es: "En carretera con nosotros" }, text: { en: "Private driver-guide, 24/7 hotline, every booking confirmed in writing, every transfer met at the door.", fr: "Chauffeur-guide privatif, hotline 24/7, chaque réservation confirmée par écrit, chaque transfert pris en charge à la porte.", es: "Chófer-guía privado, línea 24/7, cada reserva confirmada por escrito, cada traslado a su puerta." } },
    { num: "04", title: { en: "Aftercare", fr: "Le retour", es: "El regreso" }, text: { en: "Photo & route memory book, NPS, and a credit on your next Xaluca trip if you bring friends.", fr: "Livret photo & route, NPS, et un crédit sur votre prochain voyage Xaluca si vous emmenez des amis.", es: "Libro foto y ruta, NPS y un crédito en su próximo viaje Xaluca si trae amigos." } },
  ],

  form_overline: { en: "Information capture", fr: "Demande d'informations", es: "Solicitud de información" },
  form_title: {
    en: "Tell us your dream trip",
    fr: "Racontez-nous votre voyage de rêve",
    es: "Cuéntenos su viaje soñado",
  },
  form_sub: {
    en: "Three minutes is all it takes. We will come back within 24 hours with a custom proposal and an all-inclusive quote.",
    fr: "Trois minutes suffisent. Nous revenons sous 24 heures avec une proposition sur mesure et un devis tout inclus.",
    es: "Tres minutos bastan. Le respondemos en 24 horas con una propuesta a medida y una cotización todo incluido.",
  },
  thanks: {
    en: "Trip request received — our trip designer will reach out within 24 hours.",
    fr: "Demande reçue — notre concepteur de voyage vous contacte sous 24 heures.",
    es: "Solicitud recibida — nuestro diseñador de viajes le contactará en 24 horas.",
  },
};

const TripsPage = () => {
  const { lang } = useLanguage();
  const { parse: parsePrice } = useFormatPrice();
  const formRef = useRef(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    category: "desert",
    preferred_date: "",
    guests: 2,
    notes: "",
  });
  const [status, setStatus] = useState({ submitting: false, success: false, error: false });

  const scrollToForm = (categoryKey) => {
    if (categoryKey) setForm((f) => ({ ...f, category: categoryKey }));
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      formRef.current?.querySelector("input[name='full_name']")?.focus?.();
    }, 100);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status.submitting) return;
    setStatus({ submitting: true, success: false, error: false });
    try {
      const cat = COPY.categories.find((c) => c.key === form.category);
      await axios.post(`${API}/experience-requests`, {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone || null,
        experience_key: `trip-${form.category}`,
        experience_title: cat ? (cat.title.en || cat.title.fr) : form.category,
        preferred_date: form.preferred_date || null,
        guests: Number(form.guests) || 2,
        notes: form.notes || null,
        language: lang,
        source: "trips_page",
      });
      setStatus({ submitting: false, success: true, error: false });
      setForm({ full_name: "", email: "", phone: "", category: "desert", preferred_date: "", guests: 2, notes: "" });
    } catch {
      setStatus({ submitting: false, success: false, error: true });
    }
  };

  return (
    <div data-testid="page-trips" className="bg-[#FDFBF7]">
      {/* HERO — multi-slide (video first, then journey photography) */}
      <section data-testid="trips-hero">
        <PageHero
          images={[
            HERO_IMAGE,
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg",
            "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=2400&q=80",
          ]}
          alt="Trips · Grup Xaluca"
          scope="page.trips"
          overline={pick(COPY.hero_overline, lang)}
          title={pick(COPY.hero_title, lang)}
          titleAccent={pick(COPY.hero_accent, lang)}
          sub={pick(COPY.hero_sub, lang)}
          secondaryTo="#trip-categories"
          secondaryLabel={pick(COPY.hero_secondary, lang)}
        />
      </section>

      <PageStickyNav
        items={[
          { id: "trips-intro", label: { en: "Intro", fr: "Intro", es: "Intro" } },
          { id: "trip-categories", label: { en: "Categories", fr: "Catégories", es: "Categorías" } },
          { id: "trips-process", label: { en: "Process", fr: "Processus", es: "Proceso" } },
          { id: "trips-form-section", label: { en: "Design", fr: "Concevoir", es: "Diseña" } },
          { id: "trips-closing", label: { en: "Contact", fr: "Contact", es: "Contacto" } },
        ]}
      />

      {/* INTRO + BRAND PILLARS */}
      <section id="trips-intro" className="py-20 md:py-28 scroll-mt-32" data-testid="trips-intro">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-6">
              <p className="overline">{pick(COPY.intro_overline, lang)}</p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {pick(COPY.intro_title, lang)}
              </h2>
            </div>
            <div className="md:col-span-6 flex items-end">
              <p className="text-lg md:text-xl text-[#5C5248] leading-relaxed">
                {pick(COPY.intro_text, lang)}
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 pt-10 border-t border-[#2C2621]/10">
            {COPY.brand_pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={`pillar-${i}`} className="flex flex-col" data-testid={`trips-pillar-${i}`}>
                  <div className="w-12 h-12 rounded-full bg-[#C16542]/12 text-[#C16542] flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif-x text-2xl mt-5 leading-tight text-[#2C2621]">
                    {pick(p.title, lang)}
                  </h3>
                  <p className="mt-3 text-sm text-[#5C5248] leading-relaxed">{pick(p.text, lang)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRIP CATEGORIES */}
      <section id="trip-categories" className="bg-[#F2EBE1]/50 py-24 md:py-32 border-y border-[#2C2621]/10 scroll-mt-32" data-testid="trips-categories">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-6">
              <p className="overline">{pick(COPY.cats_overline, lang)}</p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {pick(COPY.cats_title, lang)}
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-lg text-[#5C5248] leading-relaxed">{pick(COPY.cats_sub, lang)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {COPY.categories.map((c, i) => {
              const Icon = c.icon;
              return (
                <article
                  key={c.key}
                  className="bg-[#FDFBF7] border border-[#2C2621]/10 overflow-hidden flex flex-col"
                  data-testid={`trip-category-${c.key}`}
                  data-cms-scope={`trip-category.${c.key}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={c.image}
                      alt={pick(c.title, lang)}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] hover:scale-[1.05]" loading="lazy" decoding="async"
                      data-cms-role="card.image"/>
                    <div className="absolute top-4 left-4 w-11 h-11 rounded-full bg-[#1A1513]/85 text-[#D4A373] flex items-center justify-center backdrop-blur-sm">
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div className="absolute top-4 right-4 text-[10px] tracking-[0.2em] uppercase bg-[#FDFBF7]/95 text-[#C16542] px-2.5 py-1">
                      {pick(c.duration, lang)}
                    </div>
                  </div>
                  <div className="p-6 md:p-7 flex-1 flex flex-col">
                    <h3 className="font-serif-x text-2xl md:text-3xl leading-tight text-[#2C2621]">
                      {pick(c.title, lang)}
                    </h3>
                    <p className="mt-3 text-sm text-[#5C5248] leading-relaxed flex-1">
                      {pick(c.desc, lang)}
                    </p>
                    <ul className="mt-5 space-y-1.5 text-sm text-[#2C2621]">
                      {c.highlights.map((h, hi) => (
                        <li key={`h-${i}-${hi}`} className="flex items-start gap-2">
                          <Check className="w-3 h-3 text-[#C16542] mt-1 shrink-0" strokeWidth={2.2} />
                          <span>{pick(h, lang)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 pt-5 border-t border-[#2C2621]/10 flex items-center justify-between gap-3">
                      <span className="text-xs tracking-[0.15em] uppercase text-[#C16542] font-medium">
                        {parsePrice(pick(c.from, lang)) || pick(c.from, lang)}
                      </span>
                      <button
                        type="button"
                        onClick={() => scrollToForm(c.key)}
                        className="text-[11px] tracking-[0.25em] uppercase border-b border-[#2C2621]/30 hover:border-[#C16542] hover:text-[#C16542] pb-0.5 inline-flex items-center gap-1.5 transition-colors"
                        data-testid={`trip-inquire-${c.key}`}
                      >
                        {pick({ en: "Inquire", fr: "Demander", es: "Consultar" }, lang)}
                        <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW WE BUILD A TRIP */}
      <section id="trips-process" className="py-24 md:py-32 scroll-mt-32" data-testid="trips-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-7">
              <p className="overline">{pick(COPY.process_overline, lang)}</p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {pick(COPY.process_title, lang)}
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {COPY.process_steps.map((st, i) => (
              <div key={`step-${i}`} className="border-t border-[#2C2621]/15 pt-5" data-testid={`trips-step-${i}`}>
                <div className="flex items-baseline justify-between">
                  <span className="font-serif-x text-3xl text-[#C16542]">{st.num}</span>
                </div>
                <h3 className="font-serif-x text-2xl mt-4 leading-tight text-[#2C2621]">{pick(st.title, lang)}</h3>
                <p className="mt-3 text-sm text-[#5C5248] leading-relaxed">{pick(st.text, lang)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section
        ref={formRef}
        id="trips-form-section"
        className="py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7] scroll-mt-32"
        data-testid="trips-form-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="overline !text-[#D4A373] flex items-center gap-2">
              <Plane className="w-3.5 h-3.5" strokeWidth={1.5} />
              {pick(COPY.form_overline, lang)}
            </p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {pick(COPY.form_title, lang)}
            </h2>
            <p className="mt-6 text-lg text-[#FDFBF7]/75 leading-relaxed">
              {pick(COPY.form_sub, lang)}
            </p>
            <div className="mt-10 pt-6 border-t border-[#FDFBF7]/15 grid grid-cols-2 gap-x-4 gap-y-4 text-sm">
              <div>
                <p className="overline !text-[#FDFBF7]/50">{pick({ en: "Phone", fr: "Téléphone", es: "Teléfono" }, lang)}</p>
                <p className="mt-1 text-[#D4A373]">+212 535 57 84 50</p>
              </div>
              <div>
                <p className="overline !text-[#FDFBF7]/50">{pick({ en: "Email", fr: "Email", es: "Email" }, lang)}</p>
                <p className="mt-1 text-[#D4A373]">xaluca@xaluca.com</p>
              </div>
              <div className="col-span-2">
                <p className="overline !text-[#FDFBF7]/50">{pick({ en: "Agencies", fr: "Agences", es: "Agencias" }, lang)}</p>
                <p className="mt-1 text-[#FDFBF7]/85">Erfoud · Merzouga · Ouarzazate · Boumalne Dades</p>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            {status.success ? (
              <div
                className="bg-[#FDFBF7] text-[#2C2621] p-8 md:p-10 border border-[#D4A373]"
                data-testid="trips-form-success"
              >
                <p className="overline !text-[#C16542] flex items-center gap-2">
                  <Check className="w-3.5 h-3.5" strokeWidth={2.2} />
                  {pick({ en: "Thank you", fr: "Merci", es: "Gracias" }, lang)}
                </p>
                <p className="font-serif-x mt-4 text-2xl md:text-3xl leading-snug">
                  {pick(COPY.thanks, lang)}
                </p>
                <button
                  type="button"
                  onClick={() => setStatus({ submitting: false, success: false, error: false })}
                  className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#C16542] hover:text-[#A35133]"
                  data-testid="trips-form-reset"
                >
                  {pick({ en: "Send another request", fr: "Envoyer une autre demande", es: "Enviar otra solicitud" }, lang)}
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="bg-[#FDFBF7] text-[#2C2621] p-8 md:p-10 border border-[#FDFBF7]/15"
                data-testid="trips-form"
                noValidate
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <label className="block sm:col-span-2">
                    <span className="overline flex items-center gap-1.5">
                      <Compass className="w-3 h-3 text-[#C16542]" strokeWidth={1.5} />
                      {pick({ en: "Trip category", fr: "Catégorie de voyage", es: "Categoría de viaje" }, lang)} *
                    </span>
                    <select
                      required
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="trips-form-category"
                    >
                      {COPY.categories.map((c) => (
                        <option key={c.key} value={c.key}>{pick(c.title, lang)}</option>
                      ))}
                    </select>
                  </label>

                  <label className="block">
                    <span className="overline">{pick({ en: "Full name", fr: "Nom complet", es: "Nombre completo" }, lang)} *</span>
                    <input
                      type="text"
                      name="full_name"
                      required
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="trips-form-name"
                    />
                  </label>
                  <label className="block">
                    <span className="overline">{pick({ en: "Email", fr: "Email", es: "Correo" }, lang)} *</span>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="trips-form-email"
                    />
                  </label>
                  <label className="block">
                    <span className="overline">{pick({ en: "Phone (optional)", fr: "Téléphone (optionnel)", es: "Teléfono (opcional)" }, lang)}</span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="trips-form-phone"
                    />
                  </label>
                  <label className="block">
                    <span className="overline flex items-center gap-1.5">
                      <Users className="w-3 h-3 text-[#C16542]" strokeWidth={1.5} />
                      {pick({ en: "Travellers", fr: "Voyageurs", es: "Viajeros" }, lang)}
                    </span>
                    <input
                      type="number"
                      min="1"
                      max="40"
                      value={form.guests}
                      onChange={(e) => setForm({ ...form, guests: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="trips-form-guests"
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="overline flex items-center gap-1.5">
                      <Calendar className="w-3 h-3 text-[#C16542]" strokeWidth={1.5} />
                      {pick({ en: "Departure date (approx.)", fr: "Date de départ (approx.)", es: "Fecha de salida (aprox.)" }, lang)}
                    </span>
                    <input
                      type="date"
                      value={form.preferred_date}
                      onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="trips-form-date"
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="overline flex items-center gap-1.5">
                      <Heart className="w-3 h-3 text-[#C16542]" strokeWidth={1.5} />
                      {pick({ en: "Tell us about your dream trip", fr: "Parlez-nous de votre voyage de rêve", es: "Cuéntenos sobre su viaje soñado" }, lang)}
                    </span>
                    <textarea
                      rows={4}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder={pick({
                        en: "Style of stay, must-see places, food allergies, mobility, special occasions…",
                        fr: "Style de séjour, lieux incontournables, allergies alimentaires, mobilité, occasions spéciales…",
                        es: "Estilo de estancia, lugares imprescindibles, alergias alimentarias, movilidad, ocasiones especiales…",
                      }, lang)}
                      className="mt-2 w-full bg-transparent border border-[#2C2621]/20 focus:border-[#C16542] p-3 outline-none text-base resize-none placeholder:text-[#5C5248]/50"
                      data-testid="trips-form-notes"
                    />
                  </label>
                </div>

                <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-[#5C5248]">
                    {pick({
                      en: "We typically reply within 24 hours.",
                      fr: "Nous répondons généralement sous 24 heures.",
                      es: "Solemos responder en 24 horas.",
                    }, lang)}
                  </p>
                  <button
                    type="submit"
                    disabled={status.submitting}
                    className="bg-[#C16542] hover:bg-[#A35133] disabled:opacity-60 text-[#FDFBF7] px-8 py-3.5 text-xs tracking-[0.25em] uppercase inline-flex items-center justify-center gap-2"
                    data-testid="trips-form-submit"
                  >
                    {status.submitting ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                        {pick({ en: "Sending…", fr: "Envoi…", es: "Enviando…" }, lang)}
                      </>
                    ) : (
                      <>
                        {pick({ en: "Send trip request", fr: "Envoyer la demande", es: "Enviar solicitud" }, lang)}
                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </>
                    )}
                  </button>
                </div>

                {status.error && (
                  <p
                    className="mt-5 text-sm text-[#A35133] border-t border-[#A35133]/30 pt-4"
                    data-testid="trips-form-error"
                  >
                    {pick({
                      en: "Something went wrong. Please try again or write to xaluca@xaluca.com.",
                      fr: "Une erreur est survenue. Réessayez ou écrivez à xaluca@xaluca.com.",
                      es: "Algo salió mal. Inténtelo o escriba a xaluca@xaluca.com.",
                    }, lang)}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CLOSING CTA STRIP */}
      <section id="trips-closing" className="py-14 bg-[#FDFBF7] border-t border-[#2C2621]/10 scroll-mt-32" data-testid="trips-closing">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-sm tracking-[0.2em] uppercase text-[#5C5248]">
            {pick({
              en: "Prefer to chat? Our concierge is online now.",
              fr: "Préférez-vous discuter ? Notre conciergerie est en ligne.",
              es: "¿Prefiere chatear? Nuestra conserjería está en línea.",
            }, lang)}
          </p>
          <Link
            to="/contact"
            className="border border-[#2C2621]/25 hover:border-[#C16542] hover:text-[#C16542] px-6 py-3 text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2 w-fit"
            data-testid="trips-closing-cta"
          >
            {pick({ en: "Contact the trip designer", fr: "Contacter le concepteur de voyages", es: "Contactar al diseñador de viajes" }, lang)}
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TripsPage;
